import { useState, createContext, useContext, useMemo, ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Box } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FeatureFlagsProvider, useFeatureFlags } from './contexts/FeatureFlagsContext';

// Components
import ProtectedRoute from './components/common/ProtectedRoute';
// Header removed per user request - notification+profile bar no longer shown
// import Header from './components/layout/Header';
import Sidebar from './components/layout/Sidebar';
import FeatureDisabledOverlay from './components/common/FeatureDisabledOverlay';
import OmniButton from './components/common/OmniButton';
import ErrorBoundary from './components/common/ErrorBoundary';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/TradingDashboard';
import Overview from './pages/Dashboard';
import TradeEntry from './pages/TradeEntry';
import TradesList from './pages/TradesList';
import ClientsList from './pages/ClientsList';
import ClientDetail from './pages/ClientDetail';
import RiskDashboard from './pages/RiskDashboard';
import PayoutManagement from './pages/PayoutManagement';
import Analytics from './pages/Analytics';
import ProfileSettings from './pages/ProfileSettings';
import EmployeeManagement from './pages/EmployeeManagement';
import AttendanceTracking from './pages/AttendanceTracking';
import PayrollWorkflow from './pages/PayrollWorkflow';
import MyPayslips from './pages/MyPayslips';
import HrHome from './pages/HrHome';
import UserManagement from './pages/UserManagement';
import EmployeePortal from './pages/EmployeePortal';
import DealerAnalytics from './pages/DealerAnalytics';
import MarketData from './pages/MarketData';
import ImportTradesPage from './pages/ImportTradesPage';
import { ImportDropProvider } from './contexts/ImportDropContext';

import { UserRole } from './types';

// Sidebar Context for collapse state
interface SidebarContextType {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
}

export const SidebarContext = createContext<SidebarContextType>({
  collapsed: false,
  setCollapsed: () => {},
  mobileOpen: false,
  setMobileOpen: () => {},
});

export const useSidebar = () => useContext(SidebarContext);

// Trading Rabbit Theme — neutral, type-led, single accent (Stripe/Linear-inspired)
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0A0A0A',
      light: '#262626',
      dark: '#000000',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#525252',
      light: '#737373',
      dark: '#404040',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#15803D',
      light: '#22C55E',
      dark: '#166534',
    },
    warning: {
      main: '#B45309',
      light: '#D97706',
      dark: '#92400E',
    },
    error: {
      main: '#B91C1C',
      light: '#DC2626',
      dark: '#991B1B',
    },
    info: {
      main: '#1D4ED8',
      light: '#2563EB',
      dark: '#1E40AF',
    },
    background: {
      default: '#F4F4F5',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1F2937',
      secondary: '#6B7280',
    },
    divider: '#E5E7EB',
    grey: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827',
    },
  },
  typography: {
    fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: { fontWeight: 700, fontSize: '2.5rem', letterSpacing: '-0.02em', color: '#1E293B' },
    h2: { fontWeight: 700, fontSize: '2rem', letterSpacing: '-0.01em', color: '#1E293B' },
    h3: { fontWeight: 600, fontSize: '1.5rem', letterSpacing: '-0.01em', color: '#1E293B' },
    h4: { fontWeight: 600, fontSize: '1.25rem', color: '#1E293B' },
    h5: { fontWeight: 600, fontSize: '1.1rem', color: '#1E293B' },
    h6: { fontWeight: 600, fontSize: '1rem', color: '#1E293B' },
    subtitle1: { fontWeight: 500, fontSize: '1rem', color: '#64748B' },
    subtitle2: { fontWeight: 500, fontSize: '0.875rem', color: '#64748B' },
    body1: { fontSize: '0.95rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.5 },
    button: { fontWeight: 600, textTransform: 'none' as const },
    caption: { fontSize: '0.75rem', color: '#64748B' },
    overline: { fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' as const },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(15, 23, 42, 0.06)',
    '0px 1px 3px rgba(15, 23, 42, 0.1)',
    '0px 2px 4px rgba(15, 23, 42, 0.1)',
    '0px 4px 6px rgba(15, 23, 42, 0.1)',
    '0px 6px 8px rgba(15, 23, 42, 0.1)',
    '0px 8px 12px rgba(15, 23, 42, 0.1)',
    '0px 12px 16px rgba(15, 23, 42, 0.1)',
    '0px 16px 24px rgba(15, 23, 42, 0.1)',
    '0px 20px 32px rgba(15, 23, 42, 0.1)',
    '0px 24px 40px rgba(15, 23, 42, 0.1)',
    '0px 24px 48px rgba(15, 23, 42, 0.12)',
    '0px 24px 48px rgba(15, 23, 42, 0.12)',
    '0px 24px 48px rgba(15, 23, 42, 0.12)',
    '0px 24px 48px rgba(15, 23, 42, 0.12)',
    '0px 24px 48px rgba(15, 23, 42, 0.12)',
    '0px 24px 48px rgba(15, 23, 42, 0.12)',
    '0px 24px 48px rgba(15, 23, 42, 0.12)',
    '0px 24px 48px rgba(15, 23, 42, 0.12)',
    '0px 24px 48px rgba(15, 23, 42, 0.12)',
    '0px 24px 48px rgba(15, 23, 42, 0.12)',
    '0px 24px 48px rgba(15, 23, 42, 0.12)',
    '0px 24px 48px rgba(15, 23, 42, 0.12)',
    '0px 24px 48px rgba(15, 23, 42, 0.12)',
    '0px 24px 48px rgba(15, 23, 42, 0.12)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          scrollbarColor: '#CBD5E1 #F1F5F9',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#F1F5F9',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#CBD5E1',
            borderRadius: '4px',
            '&:hover': {
              background: '#94A3B8',
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: '8px',
          padding: '10px 20px',
          transition: 'all 0.2s ease-in-out',
        },
        contained: {
          boxShadow: '0px 2px 4px rgba(15, 23, 42, 0.1)',
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(15, 23, 42, 0.15)',
            transform: 'translateY(-1px)',
          },
        },
        containedPrimary: {
          background: '#0A0A0A',
          color: '#FFFFFF',
          boxShadow: 'none',
          '&:hover': {
            background: '#262626',
            boxShadow: 'none',
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: '1px solid #E5E5E5',
          borderRadius: '8px',
          transition: 'border-color 0.15s ease',
          '&:hover': {
            borderColor: '#D4D4D4',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0px 1px 3px rgba(15, 23, 42, 0.08)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: '8px',
        },
        filled: {
          '&.MuiChip-colorSuccess': {
            backgroundColor: '#ECFDF5',
            color: '#047857',
          },
          '&.MuiChip-colorError': {
            backgroundColor: '#FEF2F2',
            color: '#B91C1C',
          },
          '&.MuiChip-colorWarning': {
            backgroundColor: '#FFFBEB',
            color: '#B45309',
          },
          '&.MuiChip-colorInfo': {
            backgroundColor: '#F0F9FF',
            color: '#0369A1',
          },
          '&.MuiChip-colorPrimary': {
            backgroundColor: '#FDF2F8',
            color: '#BE185D',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            transition: 'all 0.2s ease-in-out',
            backgroundColor: '#FFFFFF',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#EC4899',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#F8FAFC',
            fontWeight: 600,
            color: '#1E293B',
            borderBottom: '2px solid #E2E8F0',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#F8FAFC',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          border: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: '#1E293B',
          boxShadow: '0px 1px 3px rgba(15, 23, 42, 0.06)',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          minHeight: '48px',
        },
      },
    },
    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      },
      styleOverrides: {
        root: {
          '& .MuiAlert-root': {
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
            fontWeight: 600,
            fontSize: '0.85rem',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
        },
        standardSuccess: {
          backgroundColor: '#ECFDF5',
          color: '#047857',
          border: '1px solid #A7F3D0',
        },
        standardError: {
          backgroundColor: '#FEF2F2',
          color: '#B91C1C',
          border: '1px solid #FECACA',
        },
        standardWarning: {
          backgroundColor: '#FFFBEB',
          color: '#B45309',
          border: '1px solid #FDE68A',
        },
        standardInfo: {
          backgroundColor: '#F0F9FF',
          color: '#0369A1',
          border: '1px solid #BAE6FD',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1E293B',
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: '8px',
          padding: '8px 12px',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '16px',
          boxShadow: '0px 20px 40px rgba(15, 23, 42, 0.2)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: '12px',
          boxShadow: '0px 8px 24px rgba(15, 23, 42, 0.15)',
          border: '1px solid #E2E8F0',
        },
      },
    },
  },
});

// React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30000,
    },
  },
});

// Layout component for authenticated pages
function AppLayout({ children }: { children: ReactNode }) {
  useSidebar();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      <Sidebar />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0, // Prevents flex child from overflowing — critical for tables/grids
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          bgcolor: '#F8FAFC',
          overflow: 'hidden', // Content stays within bounds
        }}
      >
        <Box sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2, md: 2.5 }, overflow: 'auto' }}>
          <Box sx={{ maxWidth: '1800px', mx: 'auto' }}>
            {children}
          </Box>
        </Box>
      </Box>
      {/* Omni Button for Super Admin - Feature Control */}
      <OmniButton />
    </Box>
  );
}

// Feature-gated component wrapper
interface FeatureGateProps {
  featureFlag: 'dashboard' | 'trades' | 'clients' | 'riskMonitor' | 'payouts' | 'analytics';
  featureName?: string;
  children: ReactNode;
}

// Map feature flags to display names
const featureNames: Record<string, string> = {
  dashboard: 'Dashboard',
  trades: 'Trades',
  clients: 'Clients',
  riskMonitor: 'Risk Monitor',
  payouts: 'Payouts',
  analytics: 'Analytics',
};

function FeatureGate({ featureFlag, featureName, children }: FeatureGateProps) {
  const { isFeatureEnabled } = useFeatureFlags();
  const { isSuperAdmin, user } = useAuth();

  // Super Admin can always access all features without any restriction
  if (isSuperAdmin) {
    return <>{children}</>;
  }

  // Check if feature is enabled for the current user's role
  if (isFeatureEnabled(featureFlag)) {
    return <>{children}</>;
  }

  // Admin gets read-only access - can view UI but actions are disabled
  // The useFeatureAccess hook will handle disabling actions
  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  const isAdmin = userRoles.includes(UserRole.ADMIN);
  if (isAdmin) {
    return <>{children}</>;
  }

  // For other roles - show the UI with an overlay that blocks interaction
  return (
    <FeatureDisabledOverlay
      featureName={featureName || featureNames[featureFlag]}
      showHRRedirect={true}
    >
      {children}
    </FeatureDisabledOverlay>
  );
}

// Role-based redirect component
function RoleBasedRedirect() {
  const { isAuthenticated, isLoading, isHR, isEmployee, isSuperAdmin, isAdmin, isAgent } = useAuth();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Employees (who are ONLY employees, not multi-role) are redirected to their employee portal
  if (isEmployee && !isSuperAdmin && !isAdmin && !isAgent && !isHR) {
    return <Navigate to="/employee-portal" replace />;
  }

  // HR-only users are redirected to employees page
  if (isHR && !isSuperAdmin && !isAdmin && !isAgent) {
    return <Navigate to="/employees" replace />;
  }

  // Everyone else (superadmin, admin, agent, multi-role users) goes to dashboard
  return <Navigate to="/" replace />;
}

function AppContent() {
  return (
    <BrowserRouter>
      <ImportDropProvider>
        <Routes>
        {/* Public route */}
        <Route path="/login" element={<Login />} />

        {/* Protected routes - Admin/Agent Dashboard */}
        <Route
          path="/"
          element={
            <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.AGENT, UserRole.DEALER, UserRole.CLIENT]}>
              <AppLayout>
                <FeatureGate featureFlag="dashboard">
                  <Dashboard />
                </FeatureGate>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/overview"
          element={
            <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.AGENT, UserRole.DEALER, UserRole.CLIENT]}>
              <AppLayout>
                <FeatureGate featureFlag="dashboard">
                  <Overview />
                </FeatureGate>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/trades"
          element={
            <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.AGENT, UserRole.DEALER, UserRole.CLIENT]}>
              <AppLayout>
                <FeatureGate featureFlag="trades">
                  <TradesList />
                </FeatureGate>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/trades/new"
          element={
            <Navigate to="/?newTrade=true" replace />
          }
        />
        {/* Keep old route structure for reference but redirect */}
        <Route
          path="/trades/new-legacy"
          element={
            <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.AGENT, UserRole.DEALER]}>
              <AppLayout>
                <FeatureGate featureFlag="trades">
                  <TradeEntry />
                </FeatureGate>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/trades/:id/edit"
          element={
            <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.AGENT, UserRole.DEALER]}>
              <AppLayout>
                <FeatureGate featureFlag="trades">
                  <TradeEntry />
                </FeatureGate>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/import"
          element={
            <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.AGENT, UserRole.DEALER]}>
              <AppLayout>
                <FeatureGate featureFlag="trades">
                  <ImportTradesPage />
                </FeatureGate>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/clients"
          element={
            <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.AGENT, UserRole.DEALER, UserRole.CLIENT]}>
              <AppLayout>
                <FeatureGate featureFlag="clients">
                  <ClientsList />
                </FeatureGate>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/clients/:id"
          element={
            <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.AGENT, UserRole.DEALER, UserRole.CLIENT]}>
              <AppLayout>
                <FeatureGate featureFlag="clients">
                  <ClientDetail />
                </FeatureGate>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/risk"
          element={
            <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.AGENT, UserRole.DEALER]}>
              <AppLayout>
                <FeatureGate featureFlag="riskMonitor">
                  <RiskDashboard />
                </FeatureGate>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/payouts"
          element={
            <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
              <AppLayout>
                <FeatureGate featureFlag="payouts">
                  <PayoutManagement />
                </FeatureGate>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/analytics"
          element={
            <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.AGENT, UserRole.DEALER, UserRole.CLIENT]}>
              <AppLayout>
                <FeatureGate featureFlag="analytics">
                  <Analytics />
                </FeatureGate>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dealer-analytics"
          element={
            <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.AGENT, UserRole.DEALER]}>
              <AppLayout>
                <DealerAnalytics />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/market"
          element={
            <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.AGENT, UserRole.DEALER, UserRole.CLIENT]}>
              <AppLayout>
                <FeatureGate featureFlag="dashboard">
                  <MarketData />
                </FeatureGate>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ProfileSettings />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ProfileSettings />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* HR/Payroll Module Routes - Always enabled, accessible by Admin and HR */}
        <Route
          path="/hr"
          element={
            <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.HR]}>
              <AppLayout>
                <HrHome />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/employees"
          element={
            <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.HR]}>
              <AppLayout>
                <EmployeeManagement />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/attendance"
          element={
            <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.HR]}>
              <AppLayout>
                <AttendanceTracking />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/payroll"
          element={
            <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.HR]}>
              <AppLayout>
                <PayrollWorkflow />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Legacy redirect — /salary-slips → /payroll */}
        <Route path="/salary-slips" element={<Navigate to="/payroll" replace />} />

        <Route
          path="/users"
          element={
            <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
              <AppLayout>
                <UserManagement />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Employee Portal - View own payslips (EMPLOYEES ONLY) */}
        <Route
          path="/my-payslips"
          element={
            <ProtectedRoute allowedRoles={[UserRole.EMPLOYEE]}>
              <AppLayout>
                <MyPayslips />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Employee Portal - View own data (salary slips + attendance) (EMPLOYEES ONLY) */}
        <Route
          path="/employee-portal"
          element={
            <ProtectedRoute allowedRoles={[UserRole.EMPLOYEE]}>
              <AppLayout>
                <EmployeePortal />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Catch all - redirect to dashboard or my-payslips based on role */}
        <Route path="*" element={<RoleBasedRedirect />} />
        </Routes>
      </ImportDropProvider>
    </BrowserRouter>
  );
}

function App() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const sidebarContextValue = useMemo(
    () => ({ collapsed, setCollapsed, mobileOpen, setMobileOpen }),
    [collapsed, mobileOpen]
  );

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AuthProvider>
            <FeatureFlagsProvider>
              <SidebarContext.Provider value={sidebarContextValue}>
                <AppContent />
              </SidebarContext.Provider>
            </FeatureFlagsProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
